import { useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, LayoutDashboard, Users, FileText, Receipt, BarChart3, ListChecks, MapPin, Image, AlertCircle, LogOut, Settings, Clock, TrendingUp, Search, Activity } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CompanyStore } from '@/lib/company-store';
import { getAuthState, setAuthState } from './Login';
import { logActivity, ActivityActions, Modules } from '@/lib/activity-logger';

const DashboardLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const company = CompanyStore.getCompany();
  const auth = getAuthState();

  const handleLogout = () => {
    const confirmed = window.confirm('Yakin ingin logout?');
    if (confirmed) {
      // Log logout activity
      logActivity(
        auth.user?.id || 'unknown',
        auth.user?.nama || 'Unknown User',
        ActivityActions.LOGOUT,
        Modules.AUTH,
        `${auth.user?.nama} (${auth.user?.role}) logged out`
      );
      
      setAuthState({ isLoggedIn: false, user: null });
      navigate('/login');
    }
  };

  const navItems = [
    { path: '/', label: 'Papan Pemuka', icon: LayoutDashboard },
    { path: '/global-search', label: 'Global Search', icon: Search },
    { path: '/pelanggan', label: '1. Pelanggan', icon: Users },
    { path: '/site-visit', label: '2. Lawatan Tapak', icon: MapPin },
    { path: '/sebutharga', label: '3. Sebutharga', icon: FileText },
    { path: '/invoice', label: '4. Invois', icon: Receipt },
    { path: '/nilai-kos-projek', label: '5. Nilai & Kos Projek', icon: BarChart3 },
    { path: '/kos-detail', label: '6. Butiran Kos', icon: BarChart3 },
    { path: '/hutang-pelanggan', label: '7. Hutang Pelanggan', icon: AlertCircle },
    { path: '/laporan-kerja', label: '8. Laporan Kerja', icon: ListChecks },
    { path: '/gambar-projek', label: '9. Gambar Projek', icon: Image },
    { path: '/analytics', label: 'Analytics & Insights', icon: TrendingUp },
    { path: '/system-monitor', label: 'System Monitor', icon: Activity },
    { path: '/data-backup', label: 'Backup & Restore', icon: FileText },
    { path: '/pengurusan-pengguna', label: 'Pengurusan Pengguna', icon: Settings },
    { path: '/activity-logs', label: 'Activity Logs', icon: Clock },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 text-slate-100 transform transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 lg:relative lg:w-72`}
      >
        <div className="flex flex-col h-full overflow-hidden">
          {/* Header */}
          <div className="p-6 border-b border-slate-700">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                {company.logo ? (
                  <img src={company.logo} alt={company.nama} className="w-full h-full object-contain p-1" />
                ) : (
                  <div className="w-full h-full bg-slate-200 flex items-center justify-center text-xs">Logo</div>
                )}
              </div>
              <div>
                <h1 className="font-bold text-white text-sm">{company.nama}</h1>
                <p className="text-xs text-slate-400">{company.noPendaftaran}</p>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden absolute right-4 top-6 text-slate-400 hover:text-white"
            >
              <X size={20} />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto px-4 py-6 space-y-2">
            {navItems.map((item, idx) => (
              <div key={idx}>
                {item.path ? (
                  <Link
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                      isActive(item.path)
                        ? 'bg-red-600 text-white'
                        : 'text-slate-300 hover:bg-slate-800'
                    }`}
                  >
                    <item.icon size={20} />
                    <span className="text-sm font-medium">{item.label}</span>
                  </Link>
                ) : (
                  <div className="text-slate-400 text-xs font-semibold px-4 py-2 mt-4 mb-2">
                    {item.label}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Footer */}
          <div className="border-t border-slate-700 p-4 space-y-2">
            <div className="px-4 py-2 rounded bg-slate-800/50">
              <p className="text-xs text-slate-400">Login sebagai:</p>
              <p className="text-sm font-medium text-slate-200">{auth.user?.nama || 'User'}</p>
              <p className="text-xs text-slate-500">{auth.user?.role || 'Guest'}</p>
            </div>
            <Button 
              onClick={handleLogout}
              variant="ghost" 
              className="w-full text-left text-red-400 hover:bg-red-900/20 hover:text-red-300 justify-start gap-2"
            >
              <LogOut size={16} />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <div className="h-16 bg-white border-b border-slate-200 flex items-center px-6 gap-4">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden p-2 hover:bg-slate-100 rounded-lg"
          >
            <Menu size={24} />
          </button>
          <div className="flex-1"></div>
          <div className="flex items-center gap-2">
            <div className="text-xs text-slate-500">Papan Pemuka SAP Ngecat</div>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-auto bg-slate-50">
          <div className="p-6">
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;